import Phaser from 'phaser';
import { nameText } from '../../content/names';
import type { FriendList, PlayerInfo } from '../../meta/online/schema';
import { pasteText, shareLink } from '../../platform/share';
import {
  addFriend,
  ensurePlayer,
  friendCode,
  friendLink,
  friendTarget,
  lookupFriend,
  myFriendCode,
  myFriends,
  onlineOn,
  readFriendCode,
  type FriendTarget,
  type OnlinePlayer,
} from '../online';
import { nicknameOf, whoText } from '../nicknames';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, onTap, UI } from '../ui/text';
import type { FriendData } from './FriendScene';
import { wipeTo } from './WipeScene';
import { wrapLineCount } from '../ui/wrap';

export interface FriendsData {
  /** A friend link's player: asks to add them. */
  add?: string;
}

/**
 * CLUB > YOU > FRIENDS (docs/ONLINE.md, "Boards"; docs/ONLINE_UI.md): the player's friend code and
 * link to share, pasting a friend's, and their friends: each row opens the friend's card
 * (FriendScene: how you compare, PLAY TOGETHER, NICKNAME, REMOVE, BLOCK). Friends are both ways,
 * and BOARDS can show only them. A nickname (only this player sees it) shows over their name.
 */
export class FriendsScene extends Phaser.Scene {
  private me: OnlinePlayer | null = null;
  /** The player's own secret friend code (docs/FRIENDS.md, step 3), once known. */
  private code: string | null = null;
  private list: FriendList | null = null;
  private loading = true;
  /** A player to add, asked first (from a link or a pasted code), and how they were named. */
  private asking: { info: PlayerInfo; target: FriendTarget } | null = null;
  /** A line under the buttons: what just happened. */
  private note: { text: string; color: number } | null = null;

  constructor() {
    super('Friends');
  }

  init(data: FriendsData = {}): void {
    // A link's friend is asked about once (Phaser would start the scene with it again).
    this.sys.settings.data = {};
    this.me = null;
    this.list = null;
    this.loading = true;
    this.asking = null;
    this.note = null;
    this.pendingAdd = data.add ?? null;
  }

  private pendingAdd: string | null = null;

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void this.fetchAll();
    // Who is online changes: ask again now and then (docs/FRIENDS.md).
    this.time.addEvent({
      delay: 20_000,
      loop: true,
      callback: () => {
        if (!onlineOn() || this.asking) return;
        void myFriends().then((list) => {
          if (!list || !this.scene.isActive()) return;
          this.list = list;
          this.redraw();
        });
      },
    });
  }

  private async fetchAll(): Promise<void> {
    if (!onlineOn()) {
      this.loading = false;
      this.redraw();
      return;
    }
    this.me = await ensurePlayer();
    [this.code, this.list] = await Promise.all([myFriendCode(), myFriends()]);
    this.loading = false;
    const add = this.pendingAdd ? friendTarget(this.pendingAdd) : null;
    this.pendingAdd = null;
    if (add) await this.ask(add);
    this.redraw();
  }

  /** Looks a code up and asks to add that player (or says why not). */
  private async ask(target: FriendTarget): Promise<void> {
    if ('code' in target ? target.code === this.code : target.player === this.me?.id) {
      this.note = { text: "That's your own code.", color: UI.dim };
      return;
    }
    const info = await lookupFriend(target);
    if (info === 'missing') this.note = { text: 'No player has that code.', color: UI.danger };
    else if (!info) this.note = { text: "Can't reach the server right now.", color: UI.danger };
    else if (info.player === this.me?.id)
      this.note = { text: "That's your own code.", color: UI.dim };
    else if (this.list?.friends.some((f) => f.player === info.player)) {
      this.note = {
        text: `Already friends with ${whoText(info.player, info.name)}.`,
        color: UI.dim,
      };
    } else this.asking = { info, target };
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    const top = drawClubBar(this, 'you') + 4;
    const g = this.add.graphics();
    const backY = height - TABBAR_H - 28;
    bevelButton(this, 6, backY, width - 12, 22, 'BACK', () => wipeTo(this, 'Trophy'));

    if (!onlineOn()) {
      label(this, width / 2, top + 12, 'Online scores are off.', 1, UI.accent).setOrigin(0.5, 0);
      label(this, width / 2, top + 26, 'Turn them on in Settings,', 1, UI.dim).setOrigin(0.5, 0);
      label(this, width / 2, top + 36, 'Online.', 1, UI.dim).setOrigin(0.5, 0);
      return;
    }
    if (!this.me) {
      const text = this.loading ? 'Loading...' : "Can't reach the server right now.";
      label(this, width / 2, top + 20, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }

    // The player's code, and the two ways to make a friend.
    label(this, width / 2, top, 'YOUR FRIEND CODE', 1, UI.dim).setOrigin(0.5, 0);
    label(
      this,
      width / 2,
      top + 11,
      this.code ? friendCode(this.code) : '...',
      1,
      UI.accent,
    ).setOrigin(0.5, 0);
    const half = Math.floor((width - 16) / 2);
    const by = top + 24;
    bevelButton(this, 6, by, half, 18, 'SHARE MINE', () => void this.share(), true);
    bevelButton(this, 10 + half, by, half, 18, 'PASTE CODE', () => void this.paste());
    let y = by + 24;
    if (this.note) {
      label(this, width / 2, y, this.note.text, 1, this.note.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      // A long name wraps the note to two lines: room for both.
      y += wrapLineCount(this.note.text, width - 16) * 10 + 4;
    }

    // A player to add, asked first.
    const asking = this.asking;
    if (asking) {
      navyPanel(g, 4, y, width - 8, 42);
      label(
        this,
        width / 2,
        y + 6,
        `Add ${whoText(asking.info.player, asking.info.name)}?`,
        1,
        UI.text,
      )
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 20)
        .setCenterAlign();
      bevelButton(
        this,
        10,
        y + 20,
        half - 4,
        16,
        'ADD FRIEND',
        () => void this.befriend(asking),
        true,
      );
      bevelButton(this, 10 + half, y + 20, half - 4, 16, 'NO', () => {
        this.asking = null;
        this.redraw();
      });
      y += 48;
    }

    // The friends.
    const list = this.list;
    label(this, 6, y, list ? `FRIENDS (${list.friends.length})` : 'FRIENDS', 1, UI.dim);
    y += 12;
    if (!list || list.friends.length === 0) {
      const text = !list
        ? "Can't reach the server right now."
        : 'None yet. Share your code with a friend, or paste theirs.';
      label(this, width / 2, y + 4, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }
    let shown = 0;
    // Online friends first (docs/FRIENDS.md), each with a green dot.
    const ordered = [...list.friends].sort((a, b) => Number(!!b.online) - Number(!!a.online));
    for (const f of ordered) {
      // The nickname over the name it stands for, their farthest lawn, and > to their card (the
      // whole row opens it: nickname, remove, block, and how you compare; owner, 2026-10-29).
      const nickname = nicknameOf(f.player);
      const h = 28;
      if (y + h > backY - 4 - (shown < list.friends.length - 1 ? 12 : 0)) break;
      shown++;
      navyPanel(g, 4, y, width - 8, h);
      if (f.online) g.fillStyle(UI.good).fillRect(9, y + 7, 4, 4);
      label(this, 16, y + 5, nickname ?? nameText(f.name), 1, nickname ? UI.accent : UI.text);
      if (nickname) label(this, 16, y + 16, nameText(f.name), 1, UI.dim);
      if (f.lawn) label(this, width - 30, y + 16, `Lawn ${f.lawn}`, 1, UI.dim).setOrigin(1, 0);
      const open = () =>
        wipeTo(this, 'Friend', {
          player: f.player,
          name: f.name,
          at: f.at,
          ...(f.online ? { online: true } : {}),
        } satisfies FriendData);
      const tap = this.add
        .zone(4, y, width - 30, h)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true })
        .setData('label', nickname ?? nameText(f.name));
      onTap(tap, open);
      bevelButton(this, width - 26, y + 6, 18, 16, '>', open).setData(
        'label',
        `MORE ${nickname ?? nameText(f.name)}`,
      );
      y += h + 2;
    }
    if (list.friends.length > shown) {
      label(
        this,
        width / 2,
        backY - 12,
        `and ${list.friends.length - shown} more`,
        1,
        UI.dim,
      ).setOrigin(0.5, 0);
    }
  }

  private async share(): Promise<void> {
    const code = this.code ?? (await myFriendCode());
    if (!code) {
      this.note = { text: "Can't reach the server right now.", color: UI.danger };
      this.redraw();
      return;
    }
    this.code = code;
    const outcome = await shareLink(
      'lawnmower.',
      `Be my friend on lawnmower.! My friend code: ${friendCode(code)}`,
      friendLink(code),
    );
    if (outcome === 'copied') this.note = { text: 'Link copied.', color: UI.good };
    else if (outcome === 'failed')
      this.note = { text: "Couldn't share right now.", color: UI.danger };
    this.redraw();
  }

  private async paste(): Promise<void> {
    const text = await pasteText();
    const target = text ? readFriendCode(text) : null;
    this.asking = null;
    this.note = null;
    if (!target) {
      this.note = {
        text: "No friend code there. Copy a friend's code or link first.",
        color: UI.dim,
      };
    } else {
      await this.ask(target);
    }
    this.redraw();
  }

  private async befriend(who: { info: PlayerInfo; target: FriendTarget }): Promise<void> {
    const result = await addFriend(who.target);
    this.asking = null;
    if (typeof result === 'string') {
      this.note = { text: result, color: UI.danger };
    } else if (result.pending) {
      // An old link (a player id): they accept it in their inbox.
      const name = whoText(result.info.player, result.info.name);
      this.note = { text: `Request sent to ${name}.`, color: UI.good };
    } else {
      const name = whoText(result.info.player, result.info.name);
      this.note = { text: `Friends with ${name}!`, color: UI.good };
      this.list = (await myFriends()) ?? this.list;
    }
    this.redraw();
  }
}
